/**
 * GitLab admin types & Zod schemas — Phase 13 Plan 13-03 Task 1.
 *
 * Mirrors the Rust DTOs from Plan 13-01 (services::gitlab) which serialize
 * via #[serde(rename_all = "camelCase")] — so the wire shape consumed here
 * is camelCase already. NO snake_case ↔ camelCase mapping is required at
 * the TS layer (the old admin's mapIssue / mapUser / mapProject helpers
 * are intentionally dropped — Rust handles it).
 *
 * `.strict()` is applied on every schema so an unexpected backend field
 * surfaces as a Zod error in tests rather than silently flowing through.
 *
 * Locked shape — every consumer in src/core/console/gitlab/ + the hooks
 * in src/core/console/controllers/queries/ must import from here.
 */

import { z } from 'zod'

// ─── Primitive entity schemas ───────────────────────────────────────────────

export const UserSchema = z
  .object({
    id: z.number(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string(),
  })
  .strict()
export type User = z.infer<typeof UserSchema>

export const ProjectSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    nameWithNamespace: z.string(),
    pathWithNamespace: z.string(),
    webUrl: z.string(),
    openIssuesCount: z.number(),
    // Three additional fields the Rust DTO ships (all #[serde(default)] →
    // Option<String>). Optional here so older deploys without them still
    // parse, but recognized so .strict() doesn't blow up on the keys.
    defaultBranch: z.string().nullable().optional(),
    visibility: z.string().nullable().optional(),
    lastActivityAt: z.string().nullable().optional(),
  })
  .strict()
export type Project = z.infer<typeof ProjectSchema>

export const MilestoneSchema = z
  .object({
    id: z.number(),
    iid: z.number(),
    title: z.string(),
    description: z.string(),
    state: z.enum(['active', 'closed']),
    dueDate: z.string().nullable(),
    projectId: z.number(),
  })
  .strict()
export type Milestone = z.infer<typeof MilestoneSchema>

export const IssueSchema = z
  .object({
    id: z.number(),
    iid: z.number(),
    projectId: z.number(),
    title: z.string(),
    description: z.string(),
    state: z.enum(['opened', 'closed']),
    labels: z.array(z.string()),
    milestone: MilestoneSchema.nullable(),
    assignees: z.array(UserSchema),
    assignee: UserSchema.nullable(),
    author: UserSchema,
    webUrl: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    closedAt: z.string().nullable(),
  })
  .strict()
export type Issue = z.infer<typeof IssueSchema>

// FilterState mirrors the Workspace.filterState JSONB — kept loose because
// the user adds new filter keys over time (search, labels, milestoneId,
// assigneeId, projectIds, etc). The schema is a documented allow-list.
export const FilterStateSchema = z
  .object({
    projectIds: z.array(z.number()).optional(),
    milestoneId: z.number().optional(),
    assigneeId: z.number().optional(),
    labels: z.array(z.string()).optional(),
    search: z.string().optional(),
  })
  .strict()
export type FilterState = z.infer<typeof FilterStateSchema>

export const WorkspaceSchema = z
  .object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    name: z.string(),
    projectIds: z.array(z.number()),
    // filterState is stored as JSONB on the backend; treat as an unknown
    // record on read and let the FilterStateSchema validate when it's read
    // through the filter hook. Keeping it permissive here avoids breaking
    // older workspace rows when new filter keys are added.
    filterState: z.record(z.string(), z.unknown()),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict()
export type Workspace = z.infer<typeof WorkspaceSchema>

// ─── Column / swim lane literals (verbatim from old admin) ──────────────────

export type ColumnId =
  | 'triage'
  | 'backlog'
  | 'todo'
  | 'in-progress'
  | 'review'
  | 'beta'
  | 'production'
  | 'wontfix'

export type SwimLaneType =
  | 'bug'
  | 'feature-request'
  | 'user-story'
  | 'uncategorised'

export interface ColumnDef {
  id: ColumnId
  label: string
  statusLabel: string
  color: string
}

export interface SwimLaneDef {
  type: SwimLaneType
  label: string
  typeLabel: string | null
  color: string
}

// ─── Mutation input shapes (mirror the Rust handler request bodies) ─────────

export interface CreateIssueInput {
  projectId: number
  title: string
  description?: string
  labels?: string
  milestoneId?: number
}

// Mirrors the Rust `UpdateIssueInput` (services/gitlab/client.rs) which is
// `rename_all = "camelCase"` + `Option<Vec<String>>` for label fields. Sending
// snake_case keys (`add_labels`, `remove_labels`) silently no-ops on the
// server — serde drops unknown fields — and the resulting empty PUT body
// makes GitLab return 400. Keep this typed so future call sites can't
// regress on that.
export interface UpdateIssueInput {
  title?: string
  description?: string
  addLabels?: string[]
  removeLabels?: string[]
  assigneeIds?: number[]
  milestoneId?: number | null
  stateEvent?: 'close' | 'reopen'
}

export interface CreateWorkspaceInput {
  name: string
  projectIds: number[]
  filterState?: Record<string, unknown>
}

export type UpdateWorkspaceInput = Partial<{
  name: string
  projectIds: number[]
  filterState: Record<string, unknown>
}>
